import { useCallback } from "react";

// Extend Window interface for HubSpot tracking
declare global {
  interface Window {
    _hsq?: Array<[string, ...unknown[]]>;
  }
}

// HubSpot portal ID - used to prefix custom behavioral events
// Format: pe{portal_id}_{event_name}
const HUBSPOT_PORTAL_ID = "545307";

type EventProperties = Record<string, string | number | boolean | undefined>;

/**
 * Hook for tracking custom events in HubSpot.
 *
 * Note: Custom behavioral events require HubSpot Marketing Hub Professional or higher,
 * and events must be created in HubSpot before they can be tracked.
 *
 * @example
 * const { trackEvent, identifyUser } = useHubSpotTracking();
 *
 * // Track a custom event (portal ID prefix is added automatically)
 * trackEvent('send_card_clicked', { cardCount: 5, product: 'expired' });
 * // Results in event name: pe545307_send_card_clicked
 *
 * // Identify the current user
 * identifyUser('user@example.com');
 */
export const useHubSpotTracking = () => {
  /**
   * Track a custom behavioral event in HubSpot.
   * The portal ID prefix (pe{portal_id}_) is automatically added to the event name.
   * @param eventName - The internal name of the event (must be created in HubSpot first)
   * @param properties - Optional properties to include with the event
   */
  const trackEvent = useCallback((eventName: string, properties?: EventProperties) => {
    if (typeof window === "undefined") return;

    const fullEventName = `pe${HUBSPOT_PORTAL_ID}_${eventName}`;

    console.log("[HubSpot] trackEvent:", eventName, properties);

    window._hsq = window._hsq || [];
    window._hsq.push([
      "trackCustomBehavioralEvent",
      {
        name: fullEventName,
        properties,
      },
    ]);
  }, []);

  /**
   * Identify the current user by email.
   * This associates subsequent events with the user's HubSpot contact record.
   * @param email - The user's email address
   */
  const identifyUser = useCallback((email: string) => {
    if (typeof window === "undefined") return;

    console.log("[HubSpot] identifyUser:", email);

    window._hsq = window._hsq || [];
    window._hsq.push(["identify", { email }]);
  }, []);

  /**
   * Track a page view manually.
   * Usually not needed as HubSpot tracks page views automatically,
   * but useful for SPAs when the URL changes without a full page load.
   * @param path - Optional custom path to track (defaults to current URL)
   */
  const trackPageView = useCallback((path?: string) => {
    if (typeof window === "undefined") return;

    window._hsq = window._hsq || [];
    if (path) {
      window._hsq.push(["setPath", path]);
    }
    window._hsq.push(["trackPageView"]);
  }, []);

  return {
    trackEvent,
    identifyUser,
    trackPageView,
  };
};
